'use client';

import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { useProject } from '@/features/project/hooks/use-projects';

/**
 * The single soft "~80% used" warning for a pack building — the server owns
 * the threshold via the `low` usage band on `project.pack`; the client never
 * counts anything and never shows numbers or live countdowns (pivot rule: pack
 * users see one soft warning).
 *
 * Post-extensions removal this is purely informational — there is no extension
 * SKU to up-sell, so the banner just warns. Hitting the cap surfaces an
 * info-only PACK_*_EXHAUSTED paywall from the server.
 *
 * Self-contained: reads the building from the route (all generation surfaces
 * live under /project/[projectId]/…) and the pack state from the cached
 * project detail, so call sites just drop `<PackUsageNotice kind="designs" />`
 * next to their Generate affordance. Renders nothing unless the building has
 * an active pack whose band for `kind` is exactly `low`.
 */
export function PackUsageNotice({
  kind,
  className,
}: {
  kind: 'designs' | 'albums';
  className?: string;
}) {
  const t = useTranslations('packs');
  const params = useParams<{ projectId?: string }>();
  const projectId =
    typeof params?.projectId === 'string' ? params.projectId : undefined;

  // Project detail is already cached on every generation surface; this only
  // subscribes to it (and silently backfills on a cold cache).
  const { data: projectResponse } = useProject(projectId ?? '', {
    enabled: !!projectId,
  });
  const pack = projectResponse?.data?.attributes?.pack;

  if (!projectId || !pack?.is_active) return null;
  const band = kind === 'designs' ? pack.designs_band : pack.albums_band;
  if (band !== 'low') return null;

  return (
    <NoticeBanner
      variant="warning"
      className={className}
      title={t(kind === 'designs' ? 'lowDesignsTitle' : 'lowAlbumsTitle')}
      message={t(kind === 'designs' ? 'lowDesignsMessage' : 'lowAlbumsMessage')}
    />
  );
}
